import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { blogPosts } from "@/data/blog";
import { Clock, Calendar, ArrowLeft, ArrowRight } from "lucide-react";

function buildSchema(post) {
  const BREADCRUMB = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://tripsyfy.com/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: `https://tripsyfy.com/blog/${post.slug}` },
    ],
  });

  const ARTICLE = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      "@type": "Organization",
      name: "Tripsyfy",
      url: "https://tripsyfy.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Tripsyfy",
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_9ddd47f9-cbc8-403c-b0f1-77e6c6e430be/artifacts/jabab0k2_Asset%205%404x.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://tripsyfy.com/blog/${post.slug}`,
    },
  });

  return { BREADCRUMB, ARTICLE };
}

function ContentBlock({ block }) {
  switch (block.type) {
    case "h2":
      return <h2 className="text-xl font-bold text-primary mt-10 mb-4">{block.text}</h2>;
    case "h3":
      return <h3 className="text-base font-bold text-primary mt-6 mb-2">{block.text}</h3>;
    case "p":
      return <p className="text-base text-foreground/80 leading-relaxed mb-4">{block.text}</p>;
    case "ul":
      return (
        <ul className="space-y-2 mb-5 pl-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-foreground/80 leading-relaxed">
              <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0 mt-2" />
              {item}
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div id="main-content" className="min-h-screen bg-white">
        <Navbar />
        <div className="section-padding container-wide px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h1 className="heading-lg text-primary mb-4">Article Not Found</h1>
            <p className="text-muted-foreground mb-8">We couldn't find that article. It may have been moved or removed.</p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-accent font-semibold text-sm hover:underline"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Blog
            </Link>
          </AnimatedSection>
        </div>
        <Footer />
      </div>
    );
  }

  const { BREADCRUMB, ARTICLE } = buildSchema(post);
  const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{ARTICLE}</script>
      </Helmet>
      <SeoHead
        title={post.metaTitle.replace(" | Tripsyfy", "")}
        description={post.metaDescription}
        canonical={`https://tripsyfy.com/blog/${post.slug}`}
      />
      <Navbar />

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl">
          <AnimatedSection>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent mb-6 hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Blog
            </Link>
            <h1 className="heading-xl text-white mb-5 leading-tight">{post.title}</h1>
            <div className="flex items-center gap-5">
              <span className="flex items-center gap-1.5 text-sm text-white/60">
                <Calendar className="h-4 w-4" /> {post.date}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-white/60">
                <Clock className="h-4 w-4" /> {post.readTime}
              </span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <AnimatedSection>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 border-l-4 border-accent pl-5 italic">
                {post.excerpt}
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.05}>
              {post.content.map((block, i) => (
                <ContentBlock key={i} block={block} />
              ))}
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl">
          <AnimatedSection>
            <p className="overline mb-3">Related Services</p>
            <h2 className="heading-md text-primary mb-6">How Tripsyfy can help</h2>
            <div className="flex flex-wrap gap-3 mb-8">
              {post.relatedServices.map(({ label, path }) => (
                <Link
                  key={path}
                  to={path}
                  className="inline-flex items-center gap-2 bg-white rounded-lg px-5 py-2.5 text-sm font-semibold text-primary shadow-card hover:shadow-card-hover hover:text-accent transition-all"
                >
                  {label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
            <WhatsAppCTA variant="banner" />
          </AnimatedSection>
        </div>
      </section>

      {(prevPost || nextPost) && (
        <section className="section-padding bg-white">
          <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl">
            <AnimatedSection>
              <p className="overline mb-6">More Articles</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {prevPost && (
                  <Link
                    to={`/blog/${prevPost.slug}`}
                    className="group bg-surface-subtle rounded-xl p-5 hover:shadow-card-hover transition-shadow"
                  >
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                      <ArrowLeft className="h-3 w-3" /> Previous
                    </p>
                    <p className="text-sm font-bold text-primary group-hover:text-accent transition-colors leading-snug">
                      {prevPost.title}
                    </p>
                  </Link>
                )}
                {nextPost && (
                  <Link
                    to={`/blog/${nextPost.slug}`}
                    className="group bg-surface-subtle rounded-xl p-5 hover:shadow-card-hover transition-shadow sm:text-right"
                  >
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5 sm:justify-end">
                      Next <ArrowRight className="h-3 w-3" />
                    </p>
                    <p className="text-sm font-bold text-primary group-hover:text-accent transition-colors leading-snug">
                      {nextPost.title}
                    </p>
                  </Link>
                )}
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
